import type {
  ExtendedResource,
  ExtendedResourceConstructor,
  ResourceOf,
} from '@seen/core/ExtendedResource'
import { invariant } from '@seen/core/invariant'
import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { toast } from '#src/utils/toast.ts'

export interface UseCreateMutationOptions<T extends ExtendedResource> {
  mutation?: Omit<UseMutationOptions<T, unknown, T>, 'mutationFn'>
  /**
   * Suppress the default error toast. Set this when the caller already
   * surfaces failures through a per-mutate `onError` (e.g. with a more
   * specific message), to avoid double-toasting the same failure.
   */
  disableDefaultErrorToast?: boolean
}

export function useCreateMutation<T extends ExtendedResource>(
  options?: UseCreateMutationOptions<T>
): UseMutationResult<T, unknown, T> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const isReadOnly = useIsReadOnly()

  return useMutation({
    ...options?.mutation,
    onSuccess: (data, variables, context) => {
      invariant(data.id, 'Data ID is required')
      const type = variables.fhirType
      cacheKeys.invalidateMutation(queryClient, type, data.id)
      queryClient.setQueryData(
        cacheKeys.read(type, data.id),
        data.toFhirResource()
      )
      const className = data.constructor.name
      queryClient.setQueryData(
        cacheKeys.readExtended(type, data.id, className),
        data
      )
      options?.mutation?.onSuccess?.(data, variables, context)
    },
    onError: (error, variables, context) => {
      if (!options?.disableDefaultErrorToast) {
        const message = error instanceof Error ? error.message : 'Unknown error'
        toast.error(`Failed to create ${variables.fhirType}: ${message}`)
      }
      options?.mutation?.onError?.(error, variables, context)
    },
    mutationFn: async (instance) => {
      if (isReadOnly) {
        console.warn(
          '[read-only] mutation attempted: useCreateMutation',
          instance.fhirType
        )
      }
      const resource = instance.toFhirResource()
      const result = await medplum.createResource(resource)
      return (
        instance.constructor as ExtendedResourceConstructor<T>
      ).fromFhirResource(result as ResourceOf<typeof instance>)
    },
  })
}
